import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { ResultView, Leaderboard } from "@/components/ResultView";
import { Podium } from "@/components/Podium";
import { SLIDE_META } from "@/lib/slides";
import { toast } from "sonner";
import { ArrowLeft, Users, Loader2, BarChart3, Trophy } from "lucide-react";

function topAnswer(slide, result) {
  if (!result) return null;
  const t = slide.type;
  if (t === "multiple_choice" || t === "quiz") {
    const best = [...(result.options || [])].sort((a, b) => b.count - a.count)[0];
    return best && best.count ? `${best.label} (${best.count})` : "No responses";
  }
  if (t === "word_cloud") {
    const w = (result.words || [])[0];
    return w ? `“${w.text}” ×${w.count}` : "No words";
  }
  if (t === "scales") return `Average ${result.average ?? 0}`;
  if (t === "ranking") {
    const r = (result.ranking || [])[0];
    return r ? `${r.label} (top)` : "No responses";
  }
  if (t === "open_ended") return `${(result.responses || []).length} responses`;
  if (t === "qa") return `${(result.questions || []).length} questions`;
  return null;
}

export default function Summary() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/sessions/${code}/manage`);
        setData(res.data);
      } catch (err) {
        setError(apiError(err.response?.data?.detail) || "Session not found");
      }
    })();
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
        <p className="text-slate-500">{error}</p>
        <button onClick={() => navigate("/app")} className="text-indigo-600 font-semibold">← Back to dashboard</button>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  }

  const { presentation, results, leaderboard, participant_count } = data;
  const slides = presentation?.slides || [];
  const hasQuiz = (leaderboard || []).length > 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="session-summary">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/app")} className="p-2 rounded-lg hover:bg-slate-100 transition-all" data-testid="summary-back-button">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="font-display font-bold text-lg text-slate-900 truncate">{presentation?.title} · Summary</h1>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <Users className="w-4 h-4" /> {participant_count} participants
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {hasQuiz && (
          <section className="rounded-3xl bg-slate-950 p-8 sm:p-12 overflow-hidden">
            <Podium entries={leaderboard} embedded />
          </section>
        )}

        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={<Users className="w-5 h-5" />} label="Participants" value={participant_count} />
          <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Slides" value={slides.length} />
          <StatCard icon={<Trophy className="w-5 h-5" />} label="Quiz players" value={(leaderboard || []).length} />
        </div>

        {hasQuiz && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <Leaderboard entries={leaderboard} />
          </section>
        )}

        <section className="space-y-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Per-slide results</h2>
          {slides.map((s, i) => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid={`summary-slide-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-indigo-600">{SLIDE_META[s.type].label} · Slide {i + 1}</div>
                  <h3 className="font-display font-bold text-slate-900 text-lg mt-0.5">{s.question}</h3>
                </div>
                <span className="text-sm font-medium text-slate-500 shrink-0 hidden sm:block">Top: {topAnswer(s, results?.[i])}</span>
              </div>
              <ResultView slide={s} results={results?.[i]} revealed={s.type === "quiz"} />
              {s.type === "quiz" && (results?.[i]?.leaderboard || []).length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <Leaderboard entries={results[i].leaderboard} title="This question" />
                </div>
              )}
            </div>
          ))}
        </section>

        <button onClick={() => navigate("/app")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-6 py-3 transition-all">
          Done · Back to dashboard
        </button>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">{icon}</span>
      <div>
        <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
        <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
      </div>
    </div>
  );
}
